import React from 'react';
import { Box, Typography, Button, Container } from '@mui/material';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useLenis } from '@studio-freight/react-lenis';
import { images } from '../assets/images';
import { gradients } from '../theme';
import FadeIn from './FadeIn';
import Counter from './Counter';

const Hero: React.FC = () => {
  const lenis = useLenis();

  const handleScroll = (id: string) => {
    if (lenis) {
      lenis.scrollTo(id, {
        offset: -80,
        duration: 1.2,
      });
    } else {
      const element = document.querySelector(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        overflow: 'hidden',
        backgroundColor: 'black',
      }}
    >
      {/* Background Image with Overlay */}
      <Box
        component="img"
        src={images.heroBackground}
        alt="Hero Background"
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 0,
          opacity: 0.7,
        }}
      />

      {/* Gradient Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.4) 100%)',
          zIndex: 0,
        }}
      />

      {/* Animated Glow Effects */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          right: '-10%',
          width: '50%',
          height: '50%',
          background: 'radial-gradient(circle, rgba(0, 242, 255, 0.15) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(100px)',
          zIndex: 0,
          animation: 'pulse 8s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': { opacity: 0.5 },
            '50%': { opacity: 0.8 },
          },
        }}
      />

      {/* Content */}
      <Container
        maxWidth="xl"
        sx={{
          position: 'relative',
          zIndex: 1,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          py: 8,
        }}
      >
        <Box sx={{ maxWidth: 800, mt: 8 }}>
          {/* Badge */}
          <FadeIn delay={0.1}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                padding: '10px 20px',
                mb: 2.5,
                borderRadius: '50px',
                backgroundColor: 'rgba(0, 242, 255, 0.1)',
                border: '1px solid rgba(0, 242, 255, 0.3)',
              }}
            >
              <Sparkles size={14} color="#00f2ff" />
              <Typography
                variant="caption"
                sx={{
                  color: '#00f2ff',
                  fontWeight: 600,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  fontSize: '0.7rem',
                }}
              >
                UAE's Leading PGM Recovery Partner
              </Typography>
            </Box>
          </FadeIn>

          {/* Headline */}
          <FadeIn delay={0.2}>
            <Typography
              variant="h1"
              sx={{
                ...gradients.primaryText,
                fontWeight: 700,
                mb: 2.5,
                fontSize: { xs: '2rem', md: '2.8rem' },
                lineHeight: 1.2,
              }}
            >
              Unlocking Platinum, Palladium & Rhodium from Used Catalytic Converters
            </Typography>
          </FadeIn>

          {/* Subheadline */}
          <FadeIn delay={0.3}>
            <Typography
              variant="h6"
              sx={{
                color: 'grey.300',
                mb: 3.5,
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.6,
                maxWidth: 650,
              }}
            >
              We analyze, process and refine catalytic converters using XRF and lab-grade techniques to deliver accurate, market-linked payouts.
            </Typography>
          </FadeIn>

          {/* Buttons */}
          <FadeIn delay={0.4}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                component={motion.button}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                variant="contained"
                onClick={() => handleScroll('#contact')}
                endIcon={<ArrowRight size={20} />}
                sx={{
                  background: gradients.primary,
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: '0 8px 24px -4px rgba(0, 242, 255, 0.4)',
                  '&:hover': {
                    background: gradients.primary,
                    boxShadow: '0 12px 32px -4px rgba(0, 242, 255, 0.6)',
                  },
                }}
              >
                Get Started
              </Button>
              <Button
                component={motion.button}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                variant="outlined"
                onClick={() => handleScroll('#about')}
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  backdropFilter: 'blur(10px)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  '&:hover': {
                    borderColor: '#00f2ff',
                    backgroundColor: 'rgba(0, 242, 255, 0.1)',
                  },
                }}
              >
                Learn More
              </Button>
            </Box>
          </FadeIn>

          {/* Stats Row */}
          <FadeIn delay={0.5}>
            <Box
              sx={{
                display: 'flex',
                gap: 4,
                mt: 6,
                pt: 4,
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                flexWrap: 'wrap',
              }}
            >
              {[
                { value: 120, suffix: '+', label: 'Partners' },
                { value: 500, suffix: '+', label: 'Monthly Processing' },
                { value: 99, suffix: '%', label: 'Recovery Rate' },
              ].map((stat, index) => (
                <Box key={index} sx={{ minWidth: 120 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#00f2ff',
                      fontWeight: 700,
                      mb: 0.5,
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 0.5,
                    }}
                  >
                    <Counter value={stat.value} />
                    {stat.suffix}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'grey.400',
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </FadeIn>
        </Box>
      </Container>
    </Box>
  );
};

export default Hero;
